<template>
  <div
    class="main-content wide"
    tabindex="0"
  >
    <div
      class="image"
      :style="backgroundImage"
    />
    <div class="wrap">
      <div class="content-wrap">
        <div class="top-block">
          <h1>{{ pageData.title }}</h1>
          <div
            v-dompurify-html="pageData.intro"
            class="intro"
          />

          <div class="inner-wrap">
            <div
              v-dompurify-html="pageData.description"
              class="description"
            />
            <travel-item
              v-if="pageData.bookDates"
              :type="'book'"
              :color="'white'"
              :text="pageData.bookDates"
            />

            <travel-item
              v-if="pageData.travelDates"
              :type="'travel'"
              :color="'white'"
              :text="pageData.travelDates"
            />

            <a
              v-if="showButton"
              class="open-form-link"
              :href="pageData.openFormLink.href"
            >
              {{ pageData.openFormLink.text }}
            </a>
          </div>
        </div>

        <div
          v-if="pageData.meal.title"
          class="meals-block"
        >
          <div
            v-if="getSource"
            class="image-block"
          >
            <v-lazy-image
              :src="getSource"
              :alt="getAlt"
            />
          </div>
          <div class="content-block">
            <div class="content">
              <h3>{{ pageData.meal.title }}</h3>
              <div
                v-dompurify-html="pageData.meal.description"
                class="description"
              />
            </div>
            <div
              v-if="pageData.meal.link.href"
              class="footer-block"
            >
              <div class="btn btn-blue">
                <a
                  :href="pageData.meal.link.href"
                  :target="pageData.meal.link.target"
                >{{ pageData.meal.link.text }}</a>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="showFeatures"
          class="grid-block features"
        >
          <h3>{{ pageData.features.title }}</h3>
          <div class="grid-container">
            <div
              v-for="(feature, index) in pageData.features.items"
              :key="index"
              class="feature-item"
            >
              <div class="icon-wrapper">
                <span />
                <img
                  :src="feature.img"
                  alt="icon"
                >
              </div>
              <div class="feature-text">{{ feature.text }}</div>
            </div>
          </div>
        </div>

        <div
          v-if="showSteps"
          class="grid-block process-steps"
        >
          <h3>{{ pageData.steps.title }}</h3>
          <div class="grid-container">
            <div
              v-for="(step, index) in pageData.steps.items"
              :key="index"
              class="step-item"
            >
              <span>{{ step }}</span>
            </div>
          </div>
        </div>

        <div class="special-offers">
          <h3>{{ pageData.offersTitle }}</h3>

          <offer-item
            v-for="(offer, index) in pageData.offers"
            :key="index"
            :offer="offer"
          />
        </div>

        <div
          v-if="termsAndConditions.title"
          class="terms-wrap"
        >
          <terms-and-conditions
            :title="termsAndConditions.title"
            :content="termsAndConditions.content"
          />
        </div>
      </div>

      <div
        v-if="isDesktop"
        class="sidebar"
      >
        <div
          :ref="'bookingForm'"
          class="form-wrap"
        >
          <h2> {{ pageTitle }}</h2>
          <div
            v-if="description && isTablet"
            v-dompurify-html="description"
            class="description"
          />
          <steps :active-step="activeStepIndex" />
          <group-booking-form />
        </div>

        <div
          v-if="contacts.title"
          class="contacts-snippet"
        >
          <div class="contact-title">{{ contacts.title }}</div>
          <a
            class="phone"
            :href="'tel:' + contacts.phone"
          >
            {{ contacts.phone }}
          </a>
          <div class="working-hours">{{ contacts.workingHours }}</div>
          <div class="email-title">{{ contacts.emailTitle }}</div>
          <a
            class="email"
            :href="'mailto:' + contacts.email"
          >
            {{ contacts.email }}
          </a>
          <div class="disclaimer">{{ contacts.disclaimer }}</div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapState, mapGetters } from 'vuex'
  import ScrollUtils from 'utils/scrollUtils'
  import VLazyImage from 'v-lazy-image'
  const TermsAndConditions = () => import(/* webpackChunkName: "terms-conditions" */ 'components/TermsAndConditions')
  const OfferItem = () => import(/* webpackChunkName: "offer-item" */ 'containers/Pages/GroupBooking/OfferItem')
  const TravelItem = () => import(/* webpackChunkName: "travel-item" */ 'containers/Pages/GroupBooking/TravelItem')
  const GroupBookingForm = () => import(/* webpackChunkName: "group-booking-form" */ 'components/GroupBooking/Form')
  const Steps = () => import(/* webpackChunkName: "group-booking-steps" */ 'components/GroupBooking/Steps')

  export default {
    components: {
      VLazyImage,
      TravelItem,
      OfferItem,
      TermsAndConditions,
      GroupBookingForm,
      Steps
    },

    computed: {
      ...mapGetters('device', [
        'isMobile',
        'isTablet',
        'isDesktop'
      ]),
      ...mapState('groupBooking/generic', [
        'pageTitle',
        'description',
        'activeStep'
      ]),
      ...mapState('groupBooking', [
        'pageData',
        'contacts',
        'termsAndConditions'
      ]),

      getSource () {
        if (!this.isMobile) {
          return this.pageData.meal.tabletImg.source
        }

        return this.pageData.meal.img.source
      },
      getAlt () {
        if (!this.isMobile) {
          return this.pageData.meal.tabletImg.alt
        }

        return this.pageData.meal.img.alt
      },

      backgroundImage () {
        let source = this.pageData.desktopImg

        if (this.isTablet) {
          source = this.pageData.tabletImg
        } else if (this.isMobile) {
          source = this.pageData.mobileImg
        }

        if (source) {
          return { 'background-image': `url(${source})` }
        } else {
          return { 'background-image': `` }
        }
      },

      showFeatures () {
        return this.pageData.features && this.pageData.features.items.length !== 0
      },

      showSteps () {
        return this.pageData.steps && this.pageData.steps.items.length !== 0
      },
      activeStepIndex () {
        return Number(this.activeStep.slice(-1))
      },
      showButton () {
        if (!this.isDesktop) {
          return this.pageData.openFormLink.href
        }

        return false
      }
    },
    watch: {
      activeStep() {
        const bookingForm = this.$refs.bookingForm.offsetTop
        ScrollUtils.smoothScroll(bookingForm, 300)
        if (this.isMobile) {
          ScrollUtils.enableScroll()
        }
      }
    },
    mounted () {
      document.body.setAttribute('tabindex', 1)
    }
  }

</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";

  .main-content {
    padding-bottom: 40px;
    background: $formBlock;
    @include clearfix();
  }

  .wrap {
    @include bp-desktop-small {
      padding-left: 0;

      .content-wrap {
        width: calc(100% - 315px);
        padding-right: 15px;
      }
    }
  }

  .content-wrap {
    @include bp-mf-desktop {
      width: 735px;
      float: left;
    }
  }

  .sidebar {
    width: 355px;
    float: right;
    margin-top: -50px;

    @include bp-desktop-small {
      width: 315px;

      .form-wrap {
        padding: 32px 20px 36px!important;
      }
    }

    form {
      background: grey;
      width: 100%;
      height: 100%;
    }
  }

  .form-wrap {
    min-height: 575px;
    width: 100%;
    padding: 32px 40px 36px;
    background-color: white;
    box-shadow: 0 2px 4px 0 rgba(191,191,191, 0.5);
    border-radius: 6px;

    h2 {
      color: $blue-dark;
      font-size: em(28);
      font-weight: 500;
      line-height: 36px;
      margin: 24px 24px 16px;

      @include bp-mf-tablet {
        text-align: center;
        margin: 0 auto 16px;
        font-size: em(20);
        line-height: 24px;
      }
    }

    .wrap {
      @include bp-mf-desktop {
        background-color: white;
        max-width: 355px;
        padding: 32px 42px 0;
      }
    }

    .description {
      color: $grey-default;
      font-size: em(14);
      line-height: 20px;
      text-align: center;
      margin: 0 24px 16px;

      @include bp-mf-tablet {
        text-align: center;
        max-width: 386px;
        margin-bottom: 23px;
      }
    }

  }

  .contacts-snippet {
    width: 100%;
    margin-top: 28px;
    padding: 32px 26px 27px;
    background-color: $gray-light-background;
    border-radius: 4px;
    text-align: center;
    line-height: 16px;

    .contact-title {
      color: $blue-dark;
      font-weight: 500;
      font-size: em(16);
      line-height: 19px;
    }

    .phone {
      display: inline-block;
      margin: 18px 0 7px;
      color: $grey-default;
      font-size: em(20);
      font-weight: 500;
      line-height: 24px;
      text-decoration: none;
    }

    .working-hours {
      padding-bottom: 12px;
      border-bottom: 1px solid $gray-medium-brown;
      margin-bottom: 13px;
    }

    .email {
      margin-top: 4px;
      color: $grey-default;
      font-size: em(16);
      font-weight: 500;
      line-height: 19px;
      text-decoration: none;
    }

    .disclaimer {
      color: $grey;
      font-size: em(12);
      margin-top: 16px;
    }
  }

  .image {
    height: 200px;
    background: no-repeat center center;
    background-size: cover;

    @include bp-mf-tablet {
      height: 320px;
    }
  }

  h1 {
    margin: 25px 0 16px;
    font-size: em(28);
    line-height: 36px;
    font-weight: 500;
    color: $blue-dark;

    @include bp-mf-tablet {
      margin: 40px 0 32px;
      font-size: em(48);
      line-height: 52px
    }
  }

  h3 {
    margin: 0;
    font-size: em(20);
    line-height: 24px;
    font-weight: 500;
    color: $blue-dark;
  }

  .intro {
    margin-bottom: 16px;
    font-size: em(18);
    font-weight: 300;
    line-height: 26px;

    @include bp-mf-tablet {
      margin-bottom: 24px;
      font-size: em(28);
      line-height: 38px;
    }
  }

  .description {
    font-size: em(12);
    line-height: 20px;

    @include bp-tablet-only {
      width: 420px;
    }

    @include bp-mf-tablet {
      font-size: em(16);
      line-height: 24px;
    }

    ::v-deep p {
      margin: 0;
    }
  }

  .open-form-link {
    @include text-overflow();
    display: block;
    margin-top: 24px;
    padding: 0 24px;
    height: 48px;
    line-height: 48px;
    border-radius: 4px;
    background-color: $blue-medium;
    color: white;
    font-weight: 500;
    text-decoration: none;
    text-align: center;

    @include bp-tablet-only {
      position: absolute;
      top: 0;
      right: 0;
      margin: 0;

      display: flex;
      justify-content: center;
      align-items: center;
      white-space: normal;
      max-width: calc(100% - 430px);
      line-height: normal;
    }
  }

  .top-block {
    padding: 0 24px 29px;
    border-bottom: 1px solid $gray-medium-brown;

    @include bp-mf-tablet {
      padding-bottom: 0;
      border-bottom: 0;
    }

    @include bp-mf-desktop {
      padding: 0;
    }

    .inner-wrap {
      position: relative;
      @include clearfix();
    }
  }

  .grid-block {
    padding: 28px 24px;

    @include bp-mf-tablet {
      padding: 32px 42px 40px;
      margin: 28px 26px 0;
      border-radius: 6px;
      background: white;
      box-shadow: 0 2px 4px 0 rgba(191,191,191,0.2);
    }

    @include bp-mf-desktop {
      margin: 28px 0 0;
    }
  }

  .features {
    background: white;

    .feature-item {
      margin-top: 16px;

      @include bp-mf-tablet {
        width: calc(50% - 28px);
        margin-bottom: 16px;
      }
    }

    .icon-wrapper {
      height: 54px;
      width: 54px;
      display: inline-block;
      margin-right: 16px;
      border-radius: 30px;
      text-align: center;
      background-color: white;
      box-shadow: 0 4px 15px 0 rgba(225,225,225,0.5);

      @include bp-mf-tablet {
        height: 60px;
        width: 60px;
        margin-right: 36px;
      }

      span {
        display: inline-block;
        vertical-align: middle;
        height: 100%;
      }

      img {
        vertical-align: middle;
      }
    }

    .feature-text {
      display: inline-block;
      width: calc(100% - 80px);
      margin-top: 15px;
      line-height: 22px;
      vertical-align: top;

      @include bp-mf-tablet {
        width: calc(100% - 100px);
        margin-top: 18px;
      }
    }

    .grid-container {

      @include bp-mf-tablet {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
      }
    }
  }

  .process-steps {
    counter-reset: steps;

    .step-item {
      color: $blue-dark;
      margin-top: 16px;

      @include bp-mf-tablet {
        margin-top: 24px;
        width: calc(100% / 3 - 16px);
        margin-bottom: 16px;
      }

      &:before {
        counter-increment: steps;
        content: counter(steps);
        display: inline-block;
        height: 28px;
        width: 28px;
        margin-right: 20px;
        background-color: white;
        box-shadow: 0 2px 10px 0 rgba(214,214,214,0.5);
        border-radius: 14px;
        line-height: 28px;
        text-align: center;
        vertical-align: top;
        font-weight: 500;
      }

      span {
        display: inline-block;
        width: calc(100% - 48px);
        padding-top:4px;
        line-height: 20px;
      }
    }

    .grid-container {

      @include bp-mf-tablet {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
      }
    }
  }

  .special-offers {
    margin: 28px 24px 0;
    padding-top: 28px;
    border-top: 1px solid $gray-medium-brown;

    @include bp-mf-desktop {
      margin: 28px 0 0;
    }

    h3 {
      @include bp-mf-tablet {
        font-size: em(28);
        line-height: 33px;
      }
    }

    ::v-deep .offer-item {
      margin-top: 28px;
    }
  }

  .terms-wrap {
    margin: 28px 24px 0;
    border: 1px solid $gray-light-box-border;
    border-radius: 4px;
  }

  .meals-block {
    background: white;
    border-radius: 6px;
    margin: 32px auto;
    width: calc(100% - 48px);
    box-shadow: 0 2px 4px 0 rgba(191,191,191, 0.5);
    display: flex;
    flex-direction: column;

    @include bp-mf-tablet {
      flex-direction: row;
    }

    @include bp-mf-desktop {
      width: 100%;
    }

    .image-block {
      overflow: hidden;
      position: relative;
      height: 180px;

      @include bp-mf-tablet {
        order: 1;
        width: 280px;
        height: auto;
        border-radius: 0 6px 6px 0;
      }

      .v-lazy-image {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
        max-height: 180px;

        @include bp-mf-tablet {
          max-height: none;
          max-width: 280px;
        }
      }
    }

    .content-block {

      @include bp-mf-tablet {
        width: calc(100% - 280px);
      }
    }

    .content {
      margin-bottom: 18px;
      padding: 20px 16px 0;

      @include bp-mf-tablet {
        padding: 40px 70px 0 40px;
        margin-bottom: 36px;
      }

      h3 {
        @include defaultFontValues($grey-default, 20 ,24, 500);
        margin: 0 0 12px;
        width: 100%;
      }

      .description {
        @include defaultFontValues($blue-dark, 14, 22);
        margin: 0 0 8px;
        width: 100%;
      }
    }

    .footer-block {
      padding: 0 16px 20px;

      @include bp-mf-tablet {
        padding: 0 40px 40px;
      }

      .btn {
        background: $blue-grey-white;
        height: 44px;
        padding: 0 25px;
        width: 100%;

        @include bp-mf-tablet {
          width: auto;
          max-width: 100%;
        }

        a {
          color: $blue-medium;
          font-size: em(14);
          font-weight: 500;
          line-height: 44px;
        }
      }
    }
  }

</style>
